import {
  ClockCircleOutlined,
  CrownFilled,
  ExclamationCircleOutlined,
  InboxOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { Button, Link, Tooltip } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useGlockAppCRUDActions } from '@mm/state/actions/GlockAppCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { Progress } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../../common/components/PrimarySelect/PrimarySelect';
import { TemplateGoButton } from '../../../../Automations/AutomationHome/TemplateIndex/TemplateGoButton';
import styles from './EmailDeliverabilityScore.module.css';

// tslint:disable-next-line: cyclomatic-complexity
export const EmailDeliverabilityScore = ({
  compactView,
  css,
}: {
  compactView?: boolean;
  css?: {
    container?: string;
  };
}) => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const emailAccounts = useSelector(selectActiveEmailAccounts);
  const deliverabilityScore = useSelector(
    (state: StoreState) => state.glockApp.deliverabilityScore,
  );
  const hasBusinessAccount = !!emailAccounts.find(acc => acc?.business);
  const lastTestRun =
    deliverabilityScore.lastSyncAt &&
    moment(deliverabilityScore.lastSyncAt).format('MMM Do');
  const nextTestRun =
    deliverabilityScore.nextSyncAt &&
    moment(deliverabilityScore.nextSyncAt).format('MMM Do');
  const score = deliverabilityScore.inboxRate;
  const undefinedScore = 75; // by design we show it as 75%
  const hasScore = !!score;
  const successfulScore = score && score >= 90 && lastTestRun;
  const successfulCompactView = successfulScore && compactView;
  const hasSubscription = facebookPage?.subscription;
  const status = deliverabilityScore.testStatus;
  const testInProgress = status === 'in_progress';
  const hasError = status === 'error';
  const shouldUpgrade = lastTestRun && !hasSubscription;
  const isEligibleForNextRun =
    nextTestRun &&
    moment().isSameOrAfter(moment(deliverabilityScore.nextSyncAt));
  const {
    openNewBillingModal2023,
    openActivateEmailAccountModal,
    openNewConfirmModal,
  } = useModalActions();
  const { getAccountDeliverabilityScore, runAccountDeliverabilityScore } =
    useGlockAppCRUDActions();

  const emailAccountOptions = emailAccounts
    .filter(acc => acc.business)
    .map(acc => ({
      id: acc.id.toString(),
      name: acc.emailAddress,
    }));
  const [selectedEmail, setSelectedEmail] = useState<DropdownOption>(
    emailAccountOptions?.[0],
  );

  const color = !hasScore
    ? '#BFBFBF'
    : score && score < 80
    ? '#F5222D'
    : score && score > 80 && !successfulScore
    ? '#FAAD14'
    : '#52C41A';

  const helpLink = 'https://customers.ai/blog/email-deliverability-tools';

  const onConnectEmailClick = () => {
    openActivateEmailAccountModal();
  };

  const onUpgradeClick = () => {
    openNewBillingModal2023({
      landmineName: landmineNames.EMAIL_DELIVERABILITY_UPGRADE,
    });
  };

  const onStartTest = () => {
    openNewConfirmModal({
      title: (
        <div className={styles.modalTitleContainer}>
          <div className={styles.modalTag}>Land in the Inbox every time!</div>
          <div className={styles.modalTitle}>
            {' '}
            Measure your email inbox deliverability
            <div className={styles.modalTitleSmall}>
              Reduce your emails in Spam and Promotions folders.
            </div>
          </div>
        </div>
      ),
      subtitle: (
        <div className={styles.modalBody}>
          <div>
            1. We will send emails to inboxes we monitor to measure
            deliverability.
          </div>
          <div>
            2. You will get a test results in{' '}
            <span style={{ fontWeight: 500 }}>~48 hours.</span>
          </div>
        </div>
      ),
      withImage: false,
      onResolve: async close => {
        if (facebookPage?.id && selectedEmail) {
          close();
          await runAccountDeliverabilityScore(
            facebookPage.id,
            Number(selectedEmail.id),
          );
        }
      },
      resolveButton: { children: 'Begin Test' },
      rejectButton: {
        color: 'transparent-no-shadow',
      },
      css: { container: styles.modal, cancelButton: styles.modalRejectButton },
    });
  };

  useEffect(() => {
    if (facebookPage?.id) {
      getAccountDeliverabilityScore(facebookPage.id);
    }
  }, []);

  return (
    <div
      className={classNames(styles.container, css?.container, {
        [styles.compactContainer]: compactView,
      })}
    >
      <div className={styles.scoreBlock}>
        <div
          className={classNames(styles.title, {
            [styles.compactTitle]: compactView,
          })}
        >
          Email Deliverability Score{' '}
        </div>
        {((!hasScore && !testInProgress && !!emailAccountOptions.length) ||
          isEligibleForNextRun) && (
          <PrimarySelect
            value={selectedEmail}
            options={emailAccountOptions}
            onChange={option => setSelectedEmail(option)}
            disabled={testInProgress}
          />
        )}
        <div className={styles.statsWrapper}>
          <Progress
            type="circle"
            className={styles.progressCircle}
            strokeWidth={10}
            percent={score || undefinedScore}
            format={value => (value === score ? `${value}` : '?')}
            strokeColor={color}
          />
          <div
            className={classNames(styles.stats, {
              [styles.compactStats]: compactView,
            })}
          >
            <div
              className={classNames(styles.stat, {
                [styles.compactStat]: compactView,
              })}
            >
              <InboxOutlined className={styles.icon} />
              {deliverabilityScore.inboxRate}% in Inbox
            </div>
            <div
              className={classNames(styles.stat, {
                [styles.compactStat]: compactView,
              })}
            >
              <TagOutlined className={styles.icon} />
              {deliverabilityScore.otherRate}% in Promotions
            </div>
            <div
              className={classNames(styles.stat, {
                [styles.compactStat]: compactView,
              })}
            >
              <ExclamationCircleOutlined className={styles.icon} />
              {deliverabilityScore.spamRate}% in Spam
            </div>
          </div>
        </div>
      </div>
      <div className={styles.sideNotes}>
        {successfulCompactView && (
          <div className={styles.tag}>👏 Nice work!</div>
        )}
        {lastTestRun && (
          <div>
            {deliverabilityScore.emailAddress && (
              <div className={classNames(styles.note)}>
                Account: <span>{deliverabilityScore.emailAddress}</span>
              </div>
            )}
            {lastTestRun && (
              <div className={styles.note}>
                Last test run: <span>{lastTestRun}</span>
              </div>
            )}

            {(nextTestRun || shouldUpgrade) && (
              <div
                className={classNames(styles.note, {
                  [styles.blackNote]: !hasSubscription,
                })}
              >
                Next test run: {hasSubscription && <span>{nextTestRun}</span>}
              </div>
            )}
          </div>
        )}
        {(!hasBusinessAccount ||
          (!hasScore && !testInProgress) ||
          isEligibleForNextRun) && (
          <>
            {!isEligibleForNextRun && !hasError && (
              <div className={styles.blackNote} style={{ textAlign: 'end' }}>
                Measure your email inbox deliverability with our tool
              </div>
            )}
            {hasError && (
              <div className={styles.redNote}>
                An error occurred while testing your account. Please, check your
                account and retry.
              </div>
            )}
            <Button
              onClick={() =>
                !hasBusinessAccount ? onConnectEmailClick() : onStartTest()
              }
              css={{ container: styles.primaryButton }}
              disabled={
                !hasBusinessAccount ? false : !selectedEmail || testInProgress
              }
            >
              {!hasBusinessAccount
                ? 'Connect a Work Email'
                : hasError
                ? 'Retry'
                : 'Begin Test'}
            </Button>
          </>
        )}
        {testInProgress && (
          <div
            className={classNames(styles.infoAlert, {
              [styles.infoAlertCompact]: compactView,
            })}
          >
            <ClockCircleOutlined className={styles.infoAlertIcon} /> Measuring
            deliverability can take up to 48h{' '}
          </div>
        )}
        {successfulCompactView && hasSubscription && !isEligibleForNextRun && (
          <TemplateGoButton
            label="Create Automation"
            leftIcon={<></>}
            css={{ button: styles.createAutomationBtn }}
          />
        )}

        <Tooltip
          title={
            <div>
              <Button
                color="transparent-no-shadow"
                onClick={onUpgradeClick}
                css={{
                  container: classNames(
                    styles.createAutomationBtn,
                    styles.tooltipButton,
                  ),
                }}
              >
                Upgrade to a Paid Plan
              </Button>
              <div style={{ marginTop: '8px' }}>
                to run this test continually
              </div>
            </div>
          }
        >
          {lastTestRun && !hasSubscription && (
            <div className={classNames(styles.tag, styles.featured)}>
              <CrownFilled className={styles.featuredIcon} />
              Featured
            </div>
          )}
        </Tooltip>

        {hasScore && !successfulScore && (
          <Link
            href={helpLink}
            isBlank
            className={classNames(styles.link, {
              [styles.linkCompact]: compactView,
            })}
          >
            What does this mean?
          </Link>
        )}
      </div>
    </div>
  );
};
